import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Trash } from "@/assets/icons/ds";
import { AppearTransition } from "@/components/Interface/AppearTransition/AppearTransition";
import { Button } from "@/components/Interface/Button/Button";
import { Modal } from "@/components/Interface/Modal/Modal";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { BillingAccounts } from "@/components/Portal/Documents/BillingAccounts/BillingAccounts";
import { LightboxWrapper } from "@/components/Portal/Documents/PaymentMethods/LightboxWrapper/LightboxWrapper";
import {
  paymentProviderIcons,
  paymentProviderLabels,
  PaymentProviderPicker,
} from "@/components/Portal/Documents/PaymentMethods/PaymentProviderPicker/PaymentProviderPicker";
import { useAlert } from "@/hooks/useAlert";
import { useHandleCheckoutError } from "@/hooks/useHandleCheckoutError";
import { useIsMobile } from "@/hooks/useIsMobile";
import { usePaymentMode } from "@/hooks/usePaymentMode";
import { useQueryParam } from "@/hooks/useQueryParam";
import {
  useCreateTransaction,
  useDeletePaymentMean,
  useGetCustomer,
} from "@/services/api";
import {
  DrCreditCardCardType,
  DrPaymentMeanReasonsItem,
} from "@/services/model";
import {
  AlertInterface,
  AlertTypes,
  userAccountStateAtom,
} from "@/utils/atoms";
import { isBusinessCustomer } from "@/utils/customerUtils";

// TODO: Comment back in during 1393
// import { CARD_PROVIDERS_REQUIRING_3DS, get3DsBrowserInfo } from "@/utils/lightboxHelpers";
import { RequestInvoicePanel } from "./RequestInvoicePanel";

interface Props {
  /**
   * Bills tab shows the active payment method for reference only - no
   * change/add/delete. Managing it stays exclusive to the User account tab.
   */
  readOnly?: boolean;
}

export const PaymentMethods: FC<Props> = ({ readOnly = false }) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.paymentMethods",
  });
  const { t: tCommon } = useTranslation("portal", {
    keyPrefix: "common.buttons",
  });
  const [paymentProvider, setPaymentProvider] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userAccountState, setUserAccountState] = useAtom(userAccountStateAtom);
  const { data: customer } = useGetCustomer();
  const { editingPayment } = userAccountState;

  const setAddingOrChanging = (isEditing: boolean) => {
    setUserAccountState((prev) => ({
      ...prev,
      editingPayment: isEditing,
    }));
  };
  const showAlert = useAlert();

  const [paymentStatus, setPaymentStatus] = useQueryParam("payment");
  const isSuccess = paymentStatus === "success";
  useEffect(() => {
    if (isSuccess) {
      showAlert({
        type: AlertTypes.success,
        text: t("changeSuccessAlert"),
      });
      setPaymentStatus("", { replace: true });
      setUserAccountState((prev) => ({ ...prev, editingPayment: false }));
    }
  }, [setUserAccountState, setPaymentStatus, t, showAlert, isSuccess]);

  useHandleCheckoutError(true);

  const {
    isInvoice,
    isCreditCard,
    maskedCardNumberString,
    cardType,
    noPaymentMethod,
    canDeleteCreditCard,
    refetch,
    isLoading: paymentMeansAreLoading,
  } = usePaymentMode();

  const openBill = DrPaymentMeanReasonsItem.OPEN_BILL;
  const payAsYou = DrPaymentMeanReasonsItem.PAY_AS_YOU_GO_ON;
  const serviceSettingsOn = DrPaymentMeanReasonsItem.RATED_SERVICES_SETTINGS_ON;
  const outstandingServices =
    DrPaymentMeanReasonsItem.OUTSTANDING_RATED_SERVICES_CHARGES;

  // Handle errors for deleting payment mean
  const handleErrors = (
    reasons: string | DrPaymentMeanReasonsItem[] | undefined,
  ) => {
    const alert: AlertInterface = {
      type: AlertTypes.error,
      text: t("errorAlerts.generalErrorAlert"),
    };
    if (reasons?.includes(openBill)) {
      alert.text = t("errorAlerts.openBill");
    } else if (reasons?.includes(payAsYou)) {
      alert.text = t("errorAlerts.payAsYouGo");
    } else if (reasons?.includes(serviceSettingsOn)) {
      alert.text = t("errorAlerts.serviceSettingsOn");
    } else if (reasons?.includes(outstandingServices)) {
      alert.text = t("errorAlerts.outstandingCharges");
    }

    showAlert(alert);
  };

  const { mutate: deletePaymentMean } = useDeletePaymentMean({
    mutation: {
      onSuccess: (data) => {
        if (canDeleteCreditCard === true) {
          showAlert({
            type: AlertTypes.success,
            text: t("deleteSuccessAlert"),
          });
          refetch().then(() => {
            setAddingOrChanging(false);
            setIsModalOpen(false);
          });
        }
        if (data.canDeleteCreditCard === false) {
          handleErrors(data.reasons);
        }
      },
      onError: (error) => handleErrors(`${error.response?.data}`),
    },
  });

  // TODO: Comment back in during 1393
  // const [cardholderName, setCardholderName] = useState("");

  const {
    data: datatransRequestDocument,
    mutate: createTransaction,
    reset: resetTransaction,
    // TODO: Comment back in during 1393
    // isLoading: isCreatingTransaction,
  } = useCreateTransaction();

  const handleAddingOrChanging = (s: DrCreditCardCardType | "") => {
    setPaymentProvider(s);
    // TODO: Comment back in during 1393
    // setCardholderName("");
    resetTransaction();
    createTransaction({ params: undefined }, {});
    // TODO: Comment back in during 1393 (was conditional: only for non-ECA/VIS; ECA/VIS triggered via handleCardholderNameBlur)
    // if (!CARD_PROVIDERS_REQUIRING_3DS.includes(s)) {
    //   createTransaction({ params: undefined }, {});
    // }
  };

  // TODO: Comment back in during 1393
  // const handleCardholderNameBlur = () => {
  //   if (
  //     paymentProvider &&
  //     CARD_PROVIDERS_REQUIRING_3DS.includes(paymentProvider) &&
  //     cardholderName &&
  //     !datatransRequestDocument &&
  //     !isCreatingTransaction
  //   ) {
  //     setIsLoading(true);
  //     createTransaction({
  //       params: { cardholderName, ...get3DsBrowserInfo() },
  //     });
  //   }
  // };

  // `editingPayment` lives in a global atom that isn't reset on navigation,
  // so a stale in-progress edit from the User account tab must not leak
  // edit-mode UI into the read-only Bills tab. Invoice customers also cannot
  // switch to another method, so a leftover flag must not open the picker.
  const isEditingPaymentMethod = editingPayment && !readOnly && !isInvoice;

  const isEditingAsBusinessCustomer =
    isEditingPaymentMethod && customer && isBusinessCustomer(customer);

  const isMobile = useIsMobile();
  // Match Login/Security and the bills list: on mobile the edit card is a
  // standalone screen, so the sibling card must not sit above the pinned
  // title bar.
  const isStandaloneMobileEdit = isMobile && isEditingPaymentMethod;
  const invoiceRequestHint = isEditingAsBusinessCustomer ? (
    <RequestInvoicePanel />
  ) : undefined;

  const actions = !readOnly && (
    <>
      <Button
        dataTestid={
          !editingPayment && noPaymentMethod
            ? "add-payment-method-button"
            : "payment-method-toggle-button"
        }
        variant={editingPayment ? "secondary" : "primary"}
        size="sm"
        onClick={() => {
          if (editingPayment) {
            setPaymentProvider("");
            resetTransaction();
            setIsLoading(false);
          }
          setAddingOrChanging(!editingPayment);
        }}
      >
        {editingPayment
          ? t("cancel")
          : noPaymentMethod
            ? t("addPaymentMethod")
            : t("change")}
      </Button>
      {editingPayment && !noPaymentMethod && (
        <Button
          dataTestid="save-payment-method-button"
          variant="primary"
          size="sm"
          disabled={!paymentProvider}
          onClick={() => {
            if (paymentProvider) {
              handleAddingOrChanging(paymentProvider as DrCreditCardCardType);
              setIsLoading(true);
            }
          }}
        >
          {tCommon("save")}
        </Button>
      )}
    </>
  );

  const mainCard = (
    <ProfileCard
      title={t("title")}
      // The default 428px cap left this narrower than its 50%-width column
      // on wider viewports - `none` lets TwoColumnLayout's grid size it.
      maxWidth="none"
      stickyHeader={isEditingPaymentMethod}
      subtitle={
        !editingPayment && noPaymentMethod ? t("noPaymentMethod") : undefined
      }
      actions={actions}
    >
      {isLoading && <LoadingSpinner />}
      {isModalOpen && (
        <Modal
          className="max-h-[500px]"
          variant="dark"
          title={t("deletePaymentmean")}
          onClose={() => setIsModalOpen(false)}
        >
          {t("confirmDelete")}
          <div className="mt-6 flex justify-end">
            <Button
              dataTestid="delete-button-modal"
              variant="primary"
              inverted
              onClick={() => {
                deletePaymentMean();
              }}
            >
              {t("deletePaymentmean")}
            </Button>
          </div>
        </Modal>
      )}
      {isCreditCard && cardType && (
        <>
          <div className="flex items-center gap-3 rounded bg-surface-quaternary p-4">
            {paymentProviderIcons[cardType]}
            <div className="flex flex-1 items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold leading-none text-foreground-primary">
                  {paymentProviderLabels[cardType]}
                </div>
                <div className="text-sm leading-none text-foreground-primary">
                  {maskedCardNumberString}
                </div>
              </div>
              {isEditingPaymentMethod && (
                <Button
                  dataTestid="delete-button"
                  ariaLabel={t("delete")}
                  variant="secondary"
                  size="sm"
                  icon={<Trash />}
                  onClick={() => setIsModalOpen(true)}
                />
              )}
            </div>
          </div>
          {isEditingPaymentMethod && (
            <hr className="h-px border-0 bg-border-tertiary" />
          )}
        </>
      )}
      {isEditingPaymentMethod && (
        <>
          <PaymentProviderPicker
            iconPlacement="inline"
            selected={paymentProvider}
            onChange={(s: DrCreditCardCardType) => {
              if (noPaymentMethod) {
                handleAddingOrChanging(s);
                setIsLoading(true);
              } else {
                setPaymentProvider(s);
              }
            }}
          />
          <LightboxWrapper
            paymentProvider={paymentProvider}
            transactionId={datatransRequestDocument?.refNo}
            sign={datatransRequestDocument?.sign}
            enabled={
              !!(
                datatransRequestDocument?.refNo &&
                datatransRequestDocument?.sign
              )
            }
            // TODO: comment back in during 1393
            // cardholderName={cardholderName}
            // onCardholderNameChange={setCardholderName}
            // onCardholderNameBlur={handleCardholderNameBlur}
          />
        </>
      )}
      {isStandaloneMobileEdit && invoiceRequestHint}
    </ProfileCard>
  );

  return (
    <section id="payment-methods">
      {paymentMeansAreLoading ? (
        <LoadingSpinner />
      ) : (
        <AppearTransition>
          {isInvoice ? (
            <BillingAccounts />
          ) : (
            <TwoColumnLayout
              rows={[
                {
                  left: mainCard,
                  right: isStandaloneMobileEdit
                    ? undefined
                    : invoiceRequestHint,
                },
              ]}
            />
          )}
        </AppearTransition>
      )}
    </section>
  );
};
